import type { Query } from 'found';
import { useCallback, useRef } from 'react';

import { routes } from '../../constants';
import { SearchResultsTab } from '../../types/search';
import searchResultsStorageManager from '../../utils/searchResultsStorageManager';
import useRouter from '../../utils/useRouter';
import { SearchCategory } from '../SearchBar/SearchBar';

const createSearchQuery = (
	queryMap: Query,
	preserveLocationQuery: boolean,
	locationQuery: Query
) => {
	// Clone to prevent mutation of original query objects
	const query: Query = preserveLocationQuery ? { ...locationQuery } : { ...queryMap };
	if (queryMap.term === '') {
		delete query.term;
	} else {
		query.term = queryMap.term;
	}

	return query;
};

const useSearchQuery = () => {
	const { router, match } = useRouter();
	// Ref captures latest match without adding it to useCallback deps,
	// so pushSearchQueryToRouter isn't recreated on every route change.
	const matchRef = useRef(match);
	matchRef.current = match;

	const pushSearchQueryToRouter = useCallback(
		(params: Query, category?: SearchCategory) => {
			const currentMatch = matchRef.current;
			const isOnLibraryPage = currentMatch.location.pathname.startsWith(`/${routes.search.root}`);
			const isOnCoursePage = currentMatch.location.pathname.startsWith(
				`/${routes.courseCatalog.root}`
			);
			const isSearchPage = isOnLibraryPage || isOnCoursePage;

			const targetIsCourseSection = category === SearchCategory.ProfessionalDevelopment;
			// Filters are section-specific (e.g. a library publisher may not exist for courses),
			// so when crossing library ↔ courses we must drop the existing query params.
			const isCrossingSections =
				(isOnLibraryPage && targetIsCourseSection) ||
				(isOnCoursePage && category === SearchCategory.Library);
			const preserveLocationQuery = isSearchPage && !isCrossingSections;

			const getLibrarySearchPathname = () => {
				const currentTab = currentMatch.params.tab as SearchResultsTab | undefined;
				const storedTab = searchResultsStorageManager.getSearchTabKey();
				const activeTab = [currentTab, storedTab].find(
					(tab): tab is SearchResultsTab => !!tab && Object.values(SearchResultsTab).includes(tab)
				);

				return activeTab ? `/${routes.search.root}/${activeTab}` : `/${routes.search.root}`;
			};

			const pathname = targetIsCourseSection
				? `/${routes.courseCatalog.root}`
				: getLibrarySearchPathname();
			const queryParams = targetIsCourseSection ? { ...params, contentType: 'course' } : params;

			router.push({
				pathname,
				query: createSearchQuery(queryParams, preserveLocationQuery, currentMatch.location.query),
			});
		},
		[router]
	);

	return { pushSearchQueryToRouter };
};

export default useSearchQuery;
export { createSearchQuery };
